import { useCallback } from 'react';
import { policy } from './types/ConsentModal.types';

export const useConsentUtilities: (options?: {
  defaultOnCloseBehaviour?: () => void;
  origin?: string;
}) => {
  handleCloseModal: (revert?: boolean) => void;
  getStringifiedConsent: (consentState: { [type: string]: boolean }) => string;
  setInputValue: (el: HTMLInputElement, value: string) => void;
  fieldsetToInput: (fieldsetClass: string) => HTMLInputElement;
  validateForm: (
    policies: policy[],
    consentState: {
      [type: string]: boolean;
    }
  ) => string[];
} = ({ defaultOnCloseBehaviour = () => {}, origin }) => {
  const convertToPascalCase: (value: string) => string = useCallback(
    (value) => `${value.slice(0, 1).toUpperCase()}${value.slice(1)}`,
    []
  );

  const handleCloseModal: (revert?: boolean) => void = useCallback(
    (revert = true) => {
      if(typeof document != 'undefined') {
        document
          .getElementById(`${convertToPascalCase(origin)}FormModalPopup`)
          .classList.remove(
            `${convertToPascalCase(origin)}FormModalPopup--visible`
          );
        defaultOnCloseBehaviour();
        if (revert) {
          setTimeout(() => {
            if(typeof document != 'undefined') {
              const revertButton = document.getElementsByClassName(
                `${origin}_form_previous`
              )[0];

              if (revertButton instanceof HTMLButtonElement) revertButton.click();
              else if (revertButton instanceof HTMLDivElement) {
                (revertButton.firstChild as HTMLButtonElement).click();
              }
            }
          }, 10);
        }
      }
    },
    [defaultOnCloseBehaviour, origin]
  );

  const getStringifiedConsent: (consentState: {
    [type: string]: boolean;
  }) => string = useCallback((consentState) => {
    const keys = Object.keys(consentState);
    if (keys?.length === 0) return '';

    let stringValue = '';
    keys.forEach((key, idx) => {
      stringValue =
        stringValue + `${idx ? '|' : ''}${idx},${consentState[key]}`;
    });
    return stringValue;
  }, []);

  const setInputValue: (el: HTMLInputElement, value: string) => void =
    useCallback((el, value) => {
      Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
      ).set.call(el, value);
      el.dispatchEvent(new Event('input', { bubbles: true }));
    }, []);

  const fieldsetToInput: (fieldsetClass: string) => HTMLInputElement =
    useCallback((fieldsetClass) => {
      return document
        .getElementsByClassName(fieldsetClass)[0]
        ?.getElementsByTagName('input')[0];
    }, []);

  const validateForm: (
    policies: policy[],
    consentState: {
      [type: string]: boolean;
    }
  ) => string[] = useCallback((policies, consentState) => {
    let newInvalidOptions = [];
    policies.forEach((pol) => {
      if (pol.type === 'checkbox') {
        if (pol.required && !consentState[pol.name]) {
          newInvalidOptions.push(pol.name);
        }
      } else {
        if (
          (pol.required && !consentState[pol.name]) ||
          consentState[pol.name] === null
        ) {
          newInvalidOptions.push(pol.name);
        }
      }
    });

    return newInvalidOptions;
  }, []);

  return {
    handleCloseModal,
    getStringifiedConsent,
    setInputValue,
    fieldsetToInput,
    validateForm,
  };
};
